import { useCallback, useMemo } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useCartContents } from '@zenbusiness/application-commons-ecommerce';
import useMemoObject from '@zenbusiness/application-commons-hooks/useMemoObject';

import { getPagePath, findNextPage, findPrevPage } from '#client/utils/flowPage';

import useFlowConfig from './flow/useFlowConfig';
import useFlowState from './flow/useFlowState';

/**
 * Checks if the current path in the browser history has specific flag set in the state.
 *
 * @returns {boolean} True if the current path has the `zbShopFlowBrowserNavigation` state, false otherwise.
 */
function checkCurrentPathForBackNavigationFlag() {
  const { state } = window?.history || {};
  const { zbShopFlowBrowserNavigation } = state?.usr || {};
  return !!zbShopFlowBrowserNavigation;
}

/**
 * @typedef {object} FlowNavigationReturn
 * @property {string|null} nextPagePath - The path of the next page, or null if not found.
 * @property {string|null} prevPagePath - The path of the previous page, or null if not found.
 * @property {Function} goToNextPage - Function to navigate to the next page.
 * @property {Function} goToPrevPage - Function to navigate to the previous page.
 */

/**
 * @typedef {object} NavigateOptions
 * @property {boolean} [replace] - Replace the current entry in the history stack.
 * @property {object} [state] - State to pass to the new location.
 */

/**
 * @typedef {object} UpdatedState
 * @property {any} [key] - Description of the key-value pairs in the updated state.
 */

/**
 * Custom hook to handle navigation between flow pages.
 *
 * @returns {FlowNavigationReturn} Memoized object containing navigation functions and current next/previous pages.
 */
function useFlowNavigation() {
  const { authenticated } = useAuthentication();
  const { entitlements = new Map(), businessEntity } = useBusinessEntity() ?? {};
  const { items: cartItems, hasServiceInFormationPlan } = useCartContents();
  const { flowPage } = useParams();
  const { id: flowId, pages, screensToSkipByService } = useFlowConfig();
  const { businessInfo, set } = useFlowState();
  const navigate = useNavigate();
  const findPageArgs = useMemo(
    () => ({
      authenticated,
      businessEntity,
      businessInfo,
      cartContents: { items: cartItems, hasServiceInFormationPlan },
      currentPage: flowPage,
      entitlements,
      pages,
      screensToSkip: screensToSkipByService
    }),
    [
      authenticated,
      businessEntity,
      businessInfo,
      cartItems,
      entitlements,
      flowPage,
      pages,
      screensToSkipByService,
      hasServiceInFormationPlan
    ]
  );

  const nextPage = useMemo(() => findNextPage(findPageArgs), [findPageArgs]);
  const nextPagePath = useMemo(
    () => getPagePath({ flowId, page: nextPage?.path }),
    [flowId, nextPage]
  );
  const prevPage = useMemo(() => findPrevPage(findPageArgs), [findPageArgs]);
  const prevPagePath = useMemo(
    () => getPagePath({ flowId, page: prevPage?.path }),
    [flowId, prevPage]
  );

  /**
   * Navigate to the next page in the flow.
   */
  const goToNextPage = useCallback(() => {
    if (nextPagePath) {
      // This must be set prior to navigation as FlowPageGate enforces a progress check
      set('lastPage', nextPage.path);

      // Add a flag to the state so when going back we can determine if the route was added to the history stack by us
      // allowing browser back navigation
      navigate(nextPagePath, {
        state: { zbShopFlowBrowserNavigation: true }
      });
    }
  }, [navigate, nextPage, nextPagePath, set]);

  /**
   * Navigate to a specific page in the flow.
   */
  const goToPage = useCallback(
    (pagePath) => {
      const newPagePath = getPagePath({ flowId, page: pagePath });
      if (newPagePath) {
        // This must be set prior to navigation as FlowPageGate enforces a progress check
        set('lastPage', pagePath);

        // Add a flag to the state so when going back we can determine if the route was added to the history stack by us
        // allowing browser back navigation
        navigate(newPagePath, {
          state: { zbShopFlowBrowserNavigation: true }
        });
      }
    },
    [navigate, set, flowId]
  );

  /**
   * Navigate to the previous page in the flow.
   */
  const goToPrevPage = useCallback(() => {
    if (prevPagePath) {
      const canUseBrowserBackNavigation = checkCurrentPathForBackNavigationFlag();
      if (canUseBrowserBackNavigation) {
        // use browser back navigation if current path contains the flag
        navigate(-1);
      } else {
        // since we couldn't figure out that the current path was added to the history stack by us, replace the current entry
        navigate(prevPagePath, { replace: true });
      }
    }
  }, [navigate, prevPagePath]);

  return useMemoObject({
    nextPage,
    prevPage,
    nextPagePath,
    prevPagePath,
    goToNextPage,
    goToPrevPage,
    goToPage
  });
}

export default useFlowNavigation;
